import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Camera, Package, Plus, Search, Truck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { DeliveryOrder, DeliveryStatus, Yard } from '../../types/stock.types';
import { deliveriesApi, yardsApi } from '../../utils/stock-api';
import { cn } from '../../components/ui/utils';

const STATUS_STYLES: Record<DeliveryStatus, string> = {
    draft: 'text-slate-700 bg-slate-100',
    pending_approval: 'text-amber-700 bg-amber-50',
    approved: 'text-blue-700 bg-blue-50',
    assigned: 'text-blue-700 bg-blue-50',
    in_progress: 'text-indigo-700 bg-indigo-50',
    pending_photos: 'text-amber-700 bg-amber-50',
    completed: 'text-emerald-700 bg-emerald-50',
    ready_to_enter: 'text-amber-700 bg-amber-50',
    entered: 'text-emerald-800 bg-emerald-100 ring-1 ring-emerald-200',
    voided: 'text-red-700 bg-red-50 ring-1 ring-red-200',
};

export const DeliveryStatusPill: React.FC<{ status: DeliveryStatus }> = ({ status }) => (
    <span className={cn(
        'inline-flex items-center text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap capitalize',
        STATUS_STYLES[status] ?? 'text-slate-700 bg-slate-100'
    )}>
        {String(status).replace(/_/g, ' ')}
    </span>
);

/** The three views §4 needs, expressed as tabs rather than three separate routes. */
type View = 'all' | 'approval' | 'mine';

/**
 * Delivery Orders list — STOCK.md §4.
 *
 * The approval queue and "List to Pick" are tabs here rather than separate pages: both
 * are this list filtered, and splitting them across routes would duplicate every column
 * and action for no gain.
 *
 * Pick progress (`4/12`) is on every row because a half-picked sheet is the thing a
 * yard manager most needs to spot without opening anything.
 */
export default function DeliveryOrdersPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_DELIVERY_ORDER);
    const navigate = useNavigate();
    const { yardId: yardIdParam } = useParams<{ yardId?: string }>();
    const scopedYardId = yardIdParam ? Number(yardIdParam) : null;

    const [orders, setOrders] = useState<DeliveryOrder[]>([]);
    const [yards, setYards] = useState<Yard[]>([]);
    // Fallback for a row the server did not stamp — every order payload carries its own
    // minPhotos now, because the requirement follows each order's fleet type.
    const [defaultMinPhotos, setDefaultMinPhotos] = useState(10);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<View>('all');
    const [search, setSearch] = useState('');

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            setOrders(await deliveriesApi.list({
                yardId: scopedYardId ? String(scopedYardId) : undefined,
                status: view === 'approval' ? 'pending_approval' : undefined,
                mine: view === 'mine' ? 'true' : undefined,
            }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load deliveries');
        } finally {
            setLoading(false);
        }
    }, [scopedYardId, view]);

    useEffect(() => { if (canRead) fetchOrders(); }, [canRead, fetchOrders]);

    useEffect(() => {
        if (!canRead) return;
        yardsApi.list({ status: 'open' }).then(setYards).catch(() => undefined);
        deliveriesApi.meta().then((m) => setDefaultMinPhotos(m.minPhotos)).catch(() => undefined);
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter((o) =>
            [o.orderNumber, o.externalRef, o.project?.projectName, o.project?.projectNumber,
             o.builder, o.driver, o.regPlate]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [orders, search]);

    if (!canRead) return <AccessRestricted />;

    const scopedYard = scopedYardId ? yards.find((y) => y.id === scopedYardId) ?? null : null;
    const detailHref = (id: number) =>
        scopedYardId ? `/stock/yards/${scopedYardId}/deliveries/${id}` : `/stock/deliveries/${id}`;
    const createHref = scopedYardId
        ? `/stock/yards/${scopedYardId}/deliveries/new`
        : '/stock/deliveries/new';

    /** The denominator on the photo counter: this order's own requirement, not a global one. */
    const requiredPhotos = (o: DeliveryOrder) => o.minPhotos ?? defaultMinPhotos;

    const pickProgress = (o: DeliveryOrder) => {
        const total = o.lineCount ?? 0;
        const done = o.pickedCount ?? 0;
        if (total === 0) return <span className="text-muted-foreground">–</span>;
        return (
            <span className={done === total ? 'text-emerald-700' : 'text-muted-foreground'}>
                {done}/{total}
            </span>
        );
    };

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Deliveries</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {scopedYard
                            ? `${scopedYard.yardName} · gear going out to project sites.`
                            : 'Gear going out from the yard to project sites.'}
                    </p>
                </div>
                <Can I={ACTIONS.CREATE} a={RESOURCES.STOCK_DELIVERY_ORDER}>
                    <Button
                        onClick={() => navigate(createHref)}
                        className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        <Plus size={16} /><span>New Delivery</span>
                    </Button>
                </Can>
            </div>

            <Tabs value={view} onValueChange={(v) => setView(v as View)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="all" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        All
                    </TabsTrigger>
                    <TabsTrigger value="approval" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Awaiting approval
                    </TabsTrigger>
                    <TabsTrigger value="mine" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        My picks
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by order number, project, customer or rego..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                    {view === 'mine'
                        ? 'Nothing assigned to you.'
                        : view === 'approval'
                            ? 'Nothing awaiting approval.'
                            : 'No deliveries yet.'}
                </div>
            ) : (
                <>
                    {/* Mobile — the pick crew lives here */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((o) => (
                            <Card key={o.id} className="p-4 active:bg-accent/40"
                                  onClick={() => navigate(detailHref(o.id))}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="font-mono font-medium">{o.orderNumber}</span>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">
                                            {o.project?.projectName ?? '–'}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            Job {o.project?.projectNumber ?? '–'}
                                        </p>
                                    </div>
                                    <DeliveryStatusPill status={o.status} />
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1.5">
                                        <Package size={14} />{pickProgress(o)}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Camera size={14} />{o.photoCount ?? 0}/{requiredPhotos(o)}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Users size={14} />{o.assigneeCount ?? 0}
                                    </span>
                                </div>
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Order Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Project</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Job Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Ship Date</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Driver / Rego</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Picked</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Photos</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Crew</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((o) => (
                                        <TableRow key={o.id} onClick={() => navigate(detailHref(o.id))}
                                                  className="cursor-pointer">
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Truck size={16} className="text-muted-foreground" />
                                                    <span className="font-mono">{o.orderNumber}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[200px]">
                                                {o.project?.projectName ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell className="font-mono">
                                                {o.project?.projectNumber ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell>{o.shipDate ?? '–'}</TableCell>
                                            <TableCell className="text-sm">
                                                {o.driver || o.regPlate
                                                    ? `${o.driver ?? '–'}${o.regPlate ? ` · ${o.regPlate}` : ''}`
                                                    : <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell>{pickProgress(o)}</TableCell>
                                            <TableCell className={cn(
                                                (o.photoCount ?? 0) >= requiredPhotos(o) ? 'text-emerald-700' : 'text-muted-foreground'
                                            )}>
                                                {o.photoCount ?? 0}/{requiredPhotos(o)}
                                            </TableCell>
                                            <TableCell>{o.assigneeCount ?? 0}</TableCell>
                                            <TableCell><DeliveryStatusPill status={o.status} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
